import { Checkbox, FormControlLabel } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { match } from 'ts-pattern'
import { DEFAULT_SAMPLING_STEPS } from 'src/components/GenerationTask/constants'
import {
  ModelSamplingControl,
  RescaleCFGSelect,
} from 'src/components/GenerationTask/MultiColumnGenerationForm/controls/InferenceConfigControl'
import {
  CfgScaleSelect,
  SamplingMethodsSelect,
  SamplingStepSelect,
} from 'src/components/GenerationTask/MultiColumnGenerationForm/controls/SamplingControl'
import Text from 'src/components/ui/Text'
import { UIInput, UITextArea } from 'src/components/ui/TextField'
import type { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { isDitV1Model, isLora, isSDModel } from 'src/utils/model'
import type { HandleChange } from 'src/utils/useForm'
import type { ModelSampling, VersionValues } from './ModelSubmitFormProvider'

const DEFAULT_CFG_SCALE = 6
const DEFAULT_MODEL_SAMPLING: ModelSampling = {
  type: 'ModelSamplingDiscrete',
  inputs: {
    sampling: 'eps',
    zsnr: false,
  },
}
const DEFAULT_RESCALE_CFG = 0

export default function ModelVersionForm(props: {
  isEditing?: boolean
  loadingEditingValues?: boolean
  versionValues?: VersionValues
  handleChange: HandleChange<keyof VersionValues>
  modelType?: GenerationModelType
}) {
  const { versionValues, handleChange, modelType } = props

  return (
    <>
      <div className="flex flex-col gap-2">
        <span className="text-primary">
          <Text i18nKey="market:submit.version-form.version-string" />
        </span>
        <UIInput
          variant="rounded-normal"
          className="w-full"
          value={versionValues?.name || ''}
          onChange={handleChange('name')}
          required
        />
      </div>
      {match(modelType)
        .when(
          it => isSDModel(it) || isDitV1Model(it),
          () => (
            <>
              <div className="flex flex-col gap-2">
                <span className="text-primary">
                  <Text i18nKey="market:submit.version-form.negative-prompts" />
                </span>
                <UITextArea
                  variant="rounded-normal"
                  className="w-full resize-none"
                  value={versionValues?.negativePrompts || ''}
                  onChange={handleChange('negativePrompts')}
                  rows={3}
                />
              </div>
              {/* FIXME: ensure initial values are loaded */}
              <ModelValuesWrapper
                handleChange={handleChange}
                versionValues={versionValues}
              />
            </>
          ),
        )
        .when(isLora, () => (
          <div className="flex flex-col gap-2">
            <span className="text-primary">
              <Text i18nKey="market:submit.trigger-words" />
            </span>
            <UITextArea
              variant="rounded-normal"
              className="w-full resize-none"
              value={versionValues?.triggerWords || ''}
              onChange={handleChange('triggerWords')}
              rows={3}
              maxLength={512}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            <span className="text-right block text-neutral-500 dark:text-neutral-400 text-xs mt-1">
              {(versionValues?.triggerWords || '').length}/512
            </span>
          </div>
        ))
        .otherwise(() => null)}
    </>
  )
}

function ModelValuesWrapper(props: {
  versionValues?: VersionValues
  handleChange: HandleChange<keyof VersionValues>
}) {
  const { t } = useTranslation(['market'])
  const { versionValues, handleChange } = props
  const [enableSamplingMethod, setEnableSamplingMethod] = useState<boolean>(
    () => versionValues?.samplingMethod != null,
  )
  const [enableSamplingSteps, setEnableSamplingSteps] = useState(
    () => versionValues?.samplingSteps != null,
  )
  const [enableCfgScale, setEnableCfgScale] = useState(
    () => versionValues?.cfgScale != null,
  )
  const [enableModelSampling, setEnableModelSampling] = useState(
    () => versionValues?.inferenceConfig?.modelSampling != null,
  )
  const [enableRescaleCfg, setEnableRescaleCfg] = useState(
    () => versionValues?.inferenceConfig?.rescaleCFG != null,
  )

  return (
    <>
      <div className="flex flex-col gap-2">
        <FormControlLabel
          checked={enableSamplingMethod}
          control={
            <Checkbox
              onChange={e => {
                setEnableSamplingMethod(e.target.checked)
                if (!e.target.checked) handleChange('samplingMethod')(null)
              }}
            />
          }
          label={t('market:submit.version-form.enable-sampling-method.label')}
        />
        <SamplingMethodsSelect
          value={versionValues?.samplingMethod || ''}
          onChange={handleChange('samplingMethod')}
          selectClassName="!max-w-[unset]"
          disabled={!enableSamplingMethod}
        />
      </div>
      <div className="flex flex-col gap-2">
        <FormControlLabel
          checked={enableSamplingSteps}
          control={
            <Checkbox
              onChange={e => {
                if (e.target.checked && !versionValues?.samplingSteps) {
                  handleChange('samplingSteps')(DEFAULT_SAMPLING_STEPS)
                }
                setEnableSamplingSteps(e.target.checked)
                if (!e.target.checked) handleChange('samplingSteps')(null)
              }}
            />
          }
          label={t('market:submit.version-form.enable-sampling-steps.label')}
        />
        <SamplingStepSelect
          value={versionValues?.samplingSteps ?? 0}
          onChange={handleChange('samplingSteps')}
          sliderClassName="!max-w-[unset]"
          disabled={!enableSamplingSteps}
        />
      </div>
      <div className="flex flex-col gap-2">
        <FormControlLabel
          checked={enableCfgScale}
          control={
            <Checkbox
              onChange={e => {
                if (e.target.checked && !versionValues?.cfgScale) {
                  handleChange('cfgScale')(DEFAULT_CFG_SCALE)
                }
                setEnableCfgScale(e.target.checked)
                if (!e.target.checked) handleChange('cfgScale')(null)
              }}
            />
          }
          label={t('market:submit.version-form.enable-cfg-scale.label')}
        />
        <CfgScaleSelect
          value={versionValues?.cfgScale ?? 0}
          onChange={handleChange('cfgScale')}
          sliderClassName="!max-w-[unset]"
          disabled={!enableCfgScale}
        />
      </div>
      <div className="flex flex-col gap-2">
        <FormControlLabel
          checked={enableModelSampling}
          control={
            <Checkbox
              onChange={e => {
                if (
                  e.target.checked &&
                  !versionValues?.inferenceConfig?.modelSampling
                ) {
                  handleChange('inferenceConfig.modelSampling')(
                    DEFAULT_MODEL_SAMPLING,
                  )
                }
                setEnableModelSampling(e.target.checked)
                if (!e.target.checked)
                  handleChange('inferenceConfig.modelSampling')(null)
              }}
            />
          }
          label={t('market:submit.version-form.enable-model-sampling.label')}
        />
        <ModelSamplingControl
          value={
            versionValues?.inferenceConfig?.modelSampling ??
            DEFAULT_MODEL_SAMPLING
          }
          onChange={handleChange('inferenceConfig.modelSampling')}
          disabled={!enableModelSampling}
        />
      </div>
      <div className="flex flex-col gap-2">
        <FormControlLabel
          checked={enableRescaleCfg}
          control={
            <Checkbox
              onChange={e => {
                if (
                  e.target.checked &&
                  !versionValues?.inferenceConfig?.rescaleCFG
                ) {
                  handleChange('inferenceConfig.rescaleCFG')(
                    DEFAULT_RESCALE_CFG,
                  )
                }
                setEnableRescaleCfg(e.target.checked)
                if (!e.target.checked)
                  handleChange('inferenceConfig.rescaleCFG')(null)
              }}
            />
          }
          label={t('market:submit.version-form.enable-rescale-cfg.label')}
        />
        <RescaleCFGSelect
          value={versionValues?.inferenceConfig?.rescaleCFG}
          onChange={handleChange('inferenceConfig.rescaleCFG')}
          disabled={!enableRescaleCfg}
        />
      </div>

      {/* Re-enable Quality Tags edit after migration completes */}
      {/* <QualityTagControl
        value={versionValues?.qualityTags}
        onChange={handleChange('qualityTags')}
      /> */}
    </>
  )
}
